import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { publicApi } from '@/lib/api';
import { HOME } from '@/constants/testIds';
import CharacterCard from '@/components/CharacterCard';
import FilterSidebar from '@/components/FilterSidebar';
import { Search, Shuffle, ChevronLeft, ChevronRight, Loader2, Sparkles, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';

const HERO_BG = '/images/hero.png';

export default function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    q: searchParams.get('q') || '',
    book: searchParams.get('book') || 'all',
    importance: searchParams.get('importance') || 'all',
    testament: searchParams.get('testament') || 'all',
    page: Number(searchParams.get('page') || 1),
    limit: 24,
  });
  const [searchInput, setSearchInput] = useState(filters.q);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cotd, setCotd] = useState(null);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.q) p.set('q', filters.q);
    if (filters.book !== 'all') p.set('book', filters.book);
    if (filters.importance !== 'all') p.set('importance', filters.importance);
    if (filters.testament !== 'all') p.set('testament', filters.testament);
    p.set('page', String(filters.page));
    p.set('limit', String(filters.limit));
    return p.toString();
  }, [filters]);

  useEffect(() => {
    publicApi.get('/books').then(({ data }) => setBooks(data.books || [])).catch(() => {});
    publicApi.get('/character-of-the-day').then(({ data }) => setCotd(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    publicApi.get(`/characters?${queryString}`, { signal: controller.signal })
            .then(({ data }) => setData(data && Array.isArray(data.items) ? data : { items: [], total: 0, pages: 1 }))
      .catch((err) => {
        if (
          axios.isCancel?.(err) ||
          err?.name === 'CanceledError' ||
          err?.code === 'ERR_CANCELED' ||
          controller.signal.aborted ||
          !err?.response // network / cors error — silent, no point in toasting
        ) return;
        toast.error('Failed to load people');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    const p = new URLSearchParams();
    if (filters.q) p.set('q', filters.q);
    if (filters.book !== 'all') p.set('book', filters.book);
    if (filters.importance !== 'all') p.set('importance', filters.importance);
    if (filters.testament !== 'all') p.set('testament', filters.testament);
    if (filters.page > 1) p.set('page', String(filters.page));
    setSearchParams(p, { replace: true });

    return () => controller.abort();
  }, [queryString]); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters((f) => ({ ...f, q: searchInput, page: 1 }));
  };

  const handleRandom = async () => {
    try {
      const { data } = await publicApi.get('/characters/random');
      navigate(`/character/${data.id}`);
    } catch {
      toast.error('No people available');
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({ q: '', book: 'all', importance: 'all', testament: 'all', page: 1, limit: 24 });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/80 to-white" />

        <div className="relative container-page py-20 lg:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#0F766E] font-semibold mb-5">
              <span className="w-8 h-px bg-[#0F766E]" />
              A scholarly directory · 66 books
            </span>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-slate-900 mb-6">
              Every person.<br />
              <span className="italic text-[#1E3A8A]">Every story.</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-10 max-w-2xl">
              Scripture Souls is a searchable, filterable index of every named person in the Bible. There are <strong>about 1,000 figures</strong> across the Old and New Testaments, from major patriarchs and prophets to the often-overlooked names threaded through every chapter.
            </p>

            <form onSubmit={handleSearch} className="flex gap-3 max-w-2xl">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" strokeWidth={2} />
                <input
                  type="text"
                  data-testid={HOME.heroSearchInput}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search names or details — e.g., 'Hagar', 'fisherman', 'Bethlehem'..."
                  className="input-base pl-12 text-base"
                />
              </div>
              <button type="submit" data-testid={HOME.heroSearchSubmit} className="btn-primary whitespace-nowrap">
                Search
              </button>
              <button
                type="button"
                onClick={handleRandom}
                data-testid={HOME.randomCharacterButton}
                className="btn-secondary whitespace-nowrap"
                title="Random person"
              >
                <Shuffle className="w-4 h-4" />
                <span className="hidden sm:inline">Random</span>
              </button>
            </form>

            {cotd?.character && (
              <Link
                to={`/character/${cotd.character.id}`}
                data-testid="character-of-the-day-card"
                className="mt-10 inline-flex items-center gap-4 max-w-2xl bg-white border border-slate-200 rounded-xl p-5 hover:border-[#0F766E] hover:-translate-y-0.5 hover:shadow-lg transition-all group"
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#CCFBF1] to-[#DBEAFE] flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-[#0F766E]" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#0F766E] font-semibold mb-0.5">
                    Person of the Day
                  </div>
                  <div className="font-serif-display text-xl text-slate-900 truncate">
                    {cotd.character.name}
                  </div>
                  <div className="text-xs text-slate-500 font-mono truncate">
                    {cotd.character.scripture_reference}
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-[#0F766E] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Directory */}
      <section className="container-page py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <FilterSidebar
              filters={filters}
              setFilters={setFilters}
              books={books}
              onClear={clearFilters}
            />
          </div>

          <div className="lg:col-span-9">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-slate-600">
                {loading ? 'Loading…' : (
                  <>
                    Showing <strong className="text-slate-900">{data.items.length}</strong> of <strong className="text-slate-900">{data.total}</strong> people
                  </>
                )}
              </p>
              {filters.q && (
                <span className="text-xs text-slate-500">
                  Results for <span className="font-mono text-[#1E3A8A]">"{filters.q}"</span>
                </span>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-32">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
              </div>
            ) : data.items.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-slate-200 rounded-xl">
                <p className="font-serif-display text-2xl text-slate-700 mb-2">No people found</p>
                <p className="text-sm text-slate-500">Try clearing filters or searching a different name.</p>
              </div>
            ) : (
              <div data-testid={HOME.characterGrid} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {data.items.map((c) => (
                  <CharacterCard key={c.id} character={c} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {data.pages > 1 && (
              <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-100">
                <button
                  data-testid={HOME.paginationPrev}
                  onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))}
                  disabled={filters.page <= 1}
                  className="btn-secondary text-sm py-2 px-4 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <span className="text-sm text-slate-600">
                  Page <strong className="text-slate-900">{filters.page}</strong> of {data.pages}
                </span>
                <button
                  data-testid={HOME.paginationNext}
                  onClick={() => setFilters((f) => ({ ...f, page: Math.min(data.pages, f.page + 1) }))}
                  disabled={filters.page >= data.pages}
                  className="btn-secondary text-sm py-2 px-4 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
